import { sysColors } from "@/assets/theme/colors";
import LitSwitch from "./LitSwitch";
import { forwardRef, useCallback, useId, type ReactNode } from "react";
import { MdSwitch } from "@material/web/switch/switch";
import mergeRefs from "../mergeRefs";

const style = {
  "--md-switch-handle-color": sysColors["sys-brand-outline"],
  "--md-switch-track-color": sysColors["sys-brand-surface-container-highest"]
} as React.CSSProperties;

export type Props = {
  "aria-describedby"?: string;
  "aria-label"?: string;
  "aria-labelledby"?: string;
  containerClassName?: string;
  disabled?: boolean;
  headline?: ReactNode;
  selected?: boolean;
  required?: boolean;
  onChange?(e: Event): void;
  supportingText?: ReactNode;
  style?: React.CSSProperties;
};

export default forwardRef<MdSwitch, Props>(function Switch(props, ref) {
  const {
    "aria-describedby": ariaDescribedBy,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    containerClassName = "",
    disabled = false,
    headline,
    required = false,
    selected = false,
    style: customStyle,
    supportingText,
    ...restProps
  } = props;
  const generatedId = useId();
  const headlineId = headline ? `${generatedId}-headline` : undefined;
  const supportingTextId = supportingText ? `${generatedId}-supporting-text` : undefined;
  const labelledBy = ariaLabel ? undefined : (ariaLabelledBy ?? headlineId);
  const describedBy = [ariaDescribedBy, supportingTextId].filter(Boolean).join(" ") || undefined;
  // Merge default style with custom style, with custom style taking precedence
  const mergedStyle = customStyle ? { ...style, ...customStyle } : style;

  // prevent the background from turning black on mobile safari
  const callbackRef = useCallback((node: MdSwitch | null) => {
    const styles = node?.shadowRoot?.styleSheets?.item(0);

    if (styles) styles.insertRule(`input:checked:disabled { background-color: initial; }`, 0);
  }, []);

  const switchElement = (
    <LitSwitch
      aria-describedby={describedBy}
      aria-label={ariaLabel}
      aria-labelledby={labelledBy}
      disabled={disabled}
      required={required}
      selected={selected}
      style={mergedStyle}
      {...restProps}
      ref={mergeRefs(ref, callbackRef)}
    />
  );

  if (!headline && !supportingText) {
    return switchElement;
  }

  return (
    <div className={`flex items-center space-x-4 ${disabled ? "opacity-50" : ""} ${containerClassName}`}>
      <div className="flex shrink-0 items-center">{switchElement}</div>
      <div>
        {headline && (
          <div id={headlineId} className="text-body-large text-sys-brand-on-surface">
            {headline}
          </div>
        )}
        {supportingText && (
          <div id={supportingTextId} className="text-body-medium text-sys-brand-on-surface-variant">
            {supportingText}
          </div>
        )}
      </div>
    </div>
  );
});
